import { AfterViewInit, ChangeDetectorRef, Component, ViewChild, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { OidcAuthService } from 'baseflow-auth';
import { of } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { SpinnerComponent, fade } from 'shared';

import { IWizardForm, IWizardStep } from '../../interfaces';
import { AppSettingsService } from '../../services';
import {
    WizardStepBaseComponent,
    WizardStepCreateAccountComponent,
    WizardStepDoneComponent,
    WizardStepLocationComponent,
    WizardStepLoginComponent,
    WizardStepTenantComponent,
} from '../wizard-step';

@Component({
    selector: 'app-onboarding-wizard',
    templateUrl: './wizard.component.html',
    styleUrls: ['./wizard.component.scss'],
    animations: [fade()],
    imports: [
        WizardStepCreateAccountComponent,
        WizardStepLoginComponent,
        WizardStepTenantComponent,
        WizardStepLocationComponent,
        WizardStepDoneComponent,
        SpinnerComponent,
        TranslatePipe,
    ],
})
export class WizardComponent implements AfterViewInit {
    private readonly oidcAuthService = inject(OidcAuthService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    public backLinkUrl: string;
    public nextDisabled = false;

    protected calculatedValues = {
        visibleStepIndex: 0,
        barFillPercentage: '0%',
    };

    @ViewChild(WizardStepCreateAccountComponent) protected stepCreateAccount: WizardStepCreateAccountComponent;
    @ViewChild(WizardStepLoginComponent) protected stepLogin: WizardStepLoginComponent;
    @ViewChild(WizardStepTenantComponent) protected stepTenant: WizardStepTenantComponent;
    @ViewChild(WizardStepLocationComponent) protected stepLocation: WizardStepLocationComponent;
    @ViewChild(WizardStepDoneComponent) protected stepDone: WizardStepDoneComponent;

    public wizardSteps: Array<IWizardStep> = [
        {
            title: 'account',
            image: 'page_1.svg',
            component: (): WizardStepBaseComponent => this.stepCreateAccount,
            optional: true,
        },
        {
            title: 'login',
            image: 'page_1.svg',
            component: (): WizardStepBaseComponent => this.stepLogin,
            optional: false,
        },
        {
            title: 'tenant',
            image: 'page_2.svg',
            component: (): WizardStepBaseComponent => this.stepTenant,
            optional: false,
        },
        {
            title: 'location',
            image: 'page_3.svg',
            component: (): WizardStepBaseComponent => this.stepLocation,
            optional: false,
        },
        {
            title: 'done',
            image: 'page_4.png',
            component: (): WizardStepBaseComponent => this.stepDone,
            optional: false,
        },
    ];

    public formData: IWizardForm = {} as IWizardForm;
    public currentStep = 0;

    public get totalSteps(): number {
        return this.wizardSteps.filter((s) => !s.optional).length;
    }

    public get step(): IWizardStep {
        return this.wizardSteps[this.currentStep];
    }

    public constructor() {
        const appSettings = inject(AppSettingsService);

        appSettings.get(tap((settings) => (this.backLinkUrl = settings.url.backLink))).subscribe();

        this.updateValues();
    }

    public ngAfterViewInit(): void {
        this.goToPageById(this.oidcAuthService.isAuthenticated() ? 'tenant' : 'account');
        this.step.component().show();

        // The active step (and its label) is only resolved here via @ViewChild, after the view was
        // first checked. Re-run change detection so the bindings settle before the dev-mode
        // verification pass, avoiding NG0100 ExpressionChangedAfterItHasBeenCheckedError.
        this.changeDetectorRef.detectChanges();
    }

    public nextStep(): void {
        this.nextDisabled = true;

        (this.step.component().validateAndSubmitForm() ?? of(true))
            .pipe(
                take(1),
                tap((isValid) => {
                    if (isValid && this.currentStep < this.wizardSteps.length - 1) {
                        this.step.component().hide();
                        this.currentStep += 1 + (this.step.skipNextSteps || 0);
                        this.step.component().show();

                        this.updateValues();
                    }
                    this.nextDisabled = false;
                })
            )
            .subscribe();
    }

    protected goToPageById(id: string): void {
        this.step.component().hide();
        this.currentStep = this.wizardSteps.findIndex((s) => s.title === id);
        this.step.component().show();

        this.updateValues();
    }

    private updateValues(): void {
        this.calculatedValues.visibleStepIndex = this.getVisibleStepIndex();
        this.calculatedValues.barFillPercentage = this.getBarFillPercentage();
    }

    private getVisibleStepIndex(): number {
        const requiredSteps = this.wizardSteps.filter((s) => !s.optional);
        const closestRequiredStep = !this.step.optional
            ? this.step
            : this.wizardSteps
                  .slice(0, this.currentStep + 1)
                  .reverse()
                  .find((e) => !e.optional);
        return requiredSteps.indexOf(closestRequiredStep) + 1;
    }

    private getBarFillPercentage(): string {
        return `${(this.calculatedValues.visibleStepIndex * 100) / this.totalSteps}%`;
    }
}
